import CookieConsentManager from "../../../../src/scripts/helpers/cookie-consent-manager";

export default class YoutubePlayer {
	static init() {
        let elements = document.querySelectorAll('.youtube-player');
        if(!elements.length) {
            return;
        }

        Array.from(elements).forEach(el => {
            new YoutubePlayer(el);
        });

        // load scripts delayed to not block critical path
        window.addEventListener('load', () => {
            CookieConsentManager.onHasConsent('youtube', () => {
                setTimeout(() => YoutubePlayer.loadScript(), 300);
            })
        });
	}

    static jsIsLoaded = false;
    static loadScript() {
        let tag = document.createElement('script');
        let firstScriptTag = document.getElementsByTagName('script')[0];

        tag.src = 'https://www.youtube.com/iframe_api';
        if (firstScriptTag) {
            firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
        } else {
            document.head.append(tag);
        }
        window.onYouTubeIframeAPIReady = () => YoutubePlayer.jsIsLoaded = true
    }

	static classes = {
        node: 'youtube-player',
		get overlay() {
			return this.node + '__overlay'
		},
		get iframe() {
			return this.node + '__iframe'
		},
		state: {
			get initialised() {
				return YoutubePlayer.classes.node + '--is-initialized'
			},
			get unstarted() {
				return YoutubePlayer.classes.node + '--is-unstarted'
			},
			get ended() {
				return YoutubePlayer.classes.node + '--is-ended'
			},
			get playing() {
				return YoutubePlayer.classes.node + '--is-playing'
			},
			get paused() {
				return YoutubePlayer.classes.node + '--is-paused'
			},
			get buffering() {
				return YoutubePlayer.classes.node + '--is-buffering'
			},
			get cued() {
				return YoutubePlayer.classes.node + '--is-cued'
			}
		}
	}

	constructor(element) {
		this.options = '';
		this.element = element;
		this.iframe = element.querySelector('.' + YoutubePlayer.classes.iframe);
		this.overlay = element.querySelector('.' + YoutubePlayer.classes.overlay);
		this.state = 'unstarted';
		this.isInitialised = false;
		this.id = this.iframe.dataset.id;

        const isNeosBackend = document.body.classList.contains('neos-backend');
        if (isNeosBackend) {
            this.setFallbackPoster();
            return;
        }

        if(!CookieConsentManager.hasConsent('youtube')) {
            this.element.querySelector('.cookie-consent').classList.remove('is-hidden');
            this.element.querySelector('.youtube-player__button--play').setAttribute('aria-hidden', 'true');
        }

        CookieConsentManager.onHasConsent('youtube', () => {
            this.element.querySelector('.cookie-consent').remove();
            this.setFallbackPoster();
            this.setFallbackPosterAlternativeText();
            this.element.querySelector('.youtube-player__button--play').setAttribute('aria-hidden', 'false');
        });

		this.overlay.addEventListener('click', () => {
            CookieConsentManager.onHasConsent('youtube', () => {
                this.startPlayerOnReady();
            });
		});
	}

    setFallbackPoster() {
        if(this.overlay.querySelector('.youtube-player__poster')) {
            return;
        }
        let youtubeId = this.iframe.getAttribute('data-id');
        let posterAlternativeText = this.element.getAttribute('data-poster-alt');
        let poster = document.createElement('img');
        poster.setAttribute('src', 'https://img.youtube.com/vi/' + youtubeId + '/sddefault.jpg')
        poster.setAttribute('class', 'youtube-player__poster');
        poster.setAttribute('alt', posterAlternativeText || 'YouTube');
        poster.setAttribute('aria-hidden', 'true');
        this.overlay.append(poster);
    }

    setFallbackPosterAlternativeText() {
        let poster = this.overlay.querySelector('img.youtube-player__poster,.youtube-player__poster img');
        if(!poster || poster.getAttribute('alt') !== 'YouTube') {
            return;
        }

        let youtubeId = this.iframe.getAttribute('data-id');
        fetch('https://www.youtube.com/oembed?url=http://www.youtube.com/watch?v=' + youtubeId + '&format=json')
            .then(response => response.json())
            .then(data => {
                poster.setAttribute('alt', 'YouTube: ' + data.title);
                const button = this.overlay.querySelector('.youtube-player__button');
                button.setAttribute('title', button.innerText + ': ' + data.title);
            })
            .catch(error => null);
    }

    startPlayerOnReady() {
        if(!YoutubePlayer.jsIsLoaded) {
            setTimeout(() => this.startPlayerOnReady(), 100);
            return;
        }

        if (this.isInitialised) {
            this.YTPlay();
        } else {
            this.setState('initialised');
            this.isInitialised = true;
            this.player = this.createPlayer(this.iframe);
        }
        this.element.querySelector('.youtube-player__iframe').focus();
    }

	createPlayer(playerNode) {
		let options = {
			videoId: this.id,
			origin: this.iframe.dataset.origin,
			hl: this.iframe.dataset.language,
			iv_load_policy: 3,
			modestbranding: 1,
			enablejsapi: 1,
			playsinline: 1,
			autohide: 1,
			controls: 0,
            playlist: this.id,
            playerVars: {rel: 0, showinfo: 0, ecver: 2}
		}

		return new YT.Player(playerNode, {
			events: {
				'onReady': () => {
					this.YTPlay();
				},
				'onStateChange': (event) => {
					switch (event.data) {
						case -1:
							this.setState('unstarted');
							break;
						case 0:
							this.setState('ended');
							break;
						case 1:
							this.setState('playing');
							break;
						case 2:
							this.setState('paused');
							break;
						case 3:
							this.setState('buffering');
							break;
						case 5:
							this.setState('cued');
							break;
					}
				}
			},
			...options
		});
	}

	setState(state) {
		let stateClass = YoutubePlayer.classes.state[state];
		if (this.element.classList.contains(stateClass)) return;

		for (let key in YoutubePlayer.classes.state) {
			if (key !== 'initialised') {
				this.element.classList.remove(YoutubePlayer.classes.state[key]);
			}
		}
		this.state = state;
		this.element.classList.add(stateClass);
	}

	YTPlay() {
		this.player.playVideo();
		this.setState('playing');
	}

	YTPause() {
		this.player.pauseVideo();
		this.setState('paused');
	}

	YTStop() {
		this.player.stopVideo();
		this.setState('stopped');
	}
}
